import React from 'react';
import { Card, CardContent, Avatar, Chip, Typography, Box } from '@mui/material';
import { Person, Warning, CheckCircle, Facebook, LinkedIn } from '@mui/icons-material';
import { colors, typography } from "../../styles/globals.js"
import _ from 'lodash';
import LinkedEntities from '../LinkedEntities/LinkedEntities.js';
import SourceButton from '../PhoneTab/SourceButton.js';
import { formatBusinessName } from '../../utils/nameFormatters.js';

const PersonCard = ({ person, variant = 'agent', onRemoveEntity, onEditEntity }) => {

    const IS_INPUT_PERSON = person.source && person.source.some(src => src.source_url === 'INPUT_DATA')

    const cardStyles = {
        width: '340px', // Reduced from 370px
        minHeight: '320px',
        height: 'auto',
        borderRadius: '16px',
        border: '1px solid #E0E2F1',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: "#F6F8FC",
        position: 'relative',
        overflow: 'visible'
    };

    const headerStyles = {
        display: 'flex',
        alignItems: 'center',
        padding: '20px 20px 16px 20px',
        gap: '12px',
    };

    const avatarStyles = {
        width: 45,
        height: 45,
        backgroundColor: variant === 'agent' ? '#0E1944' : '#4566EB',
        borderRadius: '15px',
    };

    const contentStyles = {
        padding: '0 20px 16px 20px',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
    };

    const sectionStyles = {
        marginBottom: '12px',
    };

    const labelStyles = {
        fontSize: typography.fontSizes.small,
        color: "#828592",
        // color: '#828592', // Light grey neutral color
        marginBottom: '4px',
        fontWeight: typography.fontWeights.regular,

    };

    const valueStyles = {
        fontSize: typography.fontSizes.small,
        color: colors.primary,
        fontWeight: typography.fontWeights.regular,

    };

    const statusItemStyles = {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '8px',
    };

    const twoColumnStyles = {
        display: 'flex',
        justifyContent: 'space-between',
        gap: '20px',
    };

    const columnStyles = {
        flex: 1,
    };

    const socialIconStyles = {
        color: '#0077b5',
        fontSize: '30px',
        cursor: 'pointer',
    };

    return (
        <Card style={cardStyles}>
            <Box style={headerStyles}>
                <Avatar style={avatarStyles}>
                    <Person style={{ fontSize: '30px', color: 'white' }} />
                </Avatar>
                <Box>
                    <Typography style={{ fontWeight: typography.fontWeights.regular, marginBottom: '4px' }}>
                        {person.name}
                    </Typography>
                    <Typography variant="body2" style={{ color: '#666' }}>
                        {person.title}
                    </Typography>
                </Box>
            </Box>

            <CardContent style={contentStyles}>
                {variant === 'agent' ? (
                    <>
                        {person.state && (
                            <Box style={sectionStyles}>
                                <Typography style={labelStyles}>State</Typography>
                                <Typography style={valueStyles}>{person.state}</Typography>
                            </Box>
                        )}

                        {person.addressStatus && (
                            <Box style={sectionStyles}>
                                <Typography style={labelStyles}>Rating</Typography>
                                <Box style={statusItemStyles}>
                                    <Warning style={{ color: '#ff5722', fontSize: '20px' }} />
                                    <Typography style={{ fontSize: '14px' }}>
                                        Address is residential
                                    </Typography>
                                </Box>
                                <Box style={statusItemStyles}>
                                    <CheckCircle style={{ color: '#4caf50', fontSize: '20px' }} />
                                    <Typography style={{ fontSize: '14px' }}>
                                        Address is active
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        <Box style={{ flex: 1, minHeight: '100px' }} />
                    </>
                ) : (
                    <>
                        {IS_INPUT_PERSON && (
                            <Box style={sectionStyles}>
                                <Chip
                                    label="Input Data"
                                    size="small"
                                    sx={{
                                        backgroundColor: '#EBF8FF',
                                        color: '#1E40AF',
                                        border: '1px solid #93C5FD',
                                        fontSize: '12px',
                                        fontWeight: typography.fontWeights.medium,
                                        height: '24px',
                                    }}
                                />
                            </Box>
                        )}

                        {person.connectedAttributes && person.connectedAttributes.length > 0 && (
                            <Box style={sectionStyles}>
                                <Typography style={labelStyles}>Affiliated address</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box>
                                        {person.connectedAttributes && person.connectedAttributes.length > 0 &&
                                            person.connectedAttributes.some(attr => attr && attr.trim() && attr.trim() !== 'Address:') ? (
                                            _.map(person.connectedAttributes, (attr, index) => (
                                                <Typography key={index} style={valueStyles}>
                                                    {formatBusinessName(attr.replace('Address: ', ''))}
                                                </Typography>
                                            ))
                                        ) : (
                                            <Typography style={{ ...valueStyles, color: '#6B7280' }}>
                                                N/A
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        <Box sx={{ flexShrink: 0, mb: 2 }} onClick={(e) => e.stopPropagation()}>
                            <SourceButton sources={person.source} />
                        </Box>


                        {person.socialLinks?.linkedin && (
                            <Box style={sectionStyles}>
                                <Typography style={labelStyles}>Social</Typography>
                                <LinkedIn
                                    style={socialIconStyles}
                                    onClick={() => window.open(person.socialLinks.linkedin, '_blank', 'noopener,noreferrer')}
                                />
                            </Box>
                        )}
                        {person.socialLinks?.facebook && (
                            <Box style={sectionStyles}>
                                <Typography style={labelStyles}>Social</Typography>
                                <Facebook
                                    style={socialIconStyles}
                                    onClick={() => window.open(person.socialLinks.facebook, '_blank', 'noopener,noreferrer')}
                                />
                            </Box>
                        )}
                        <Box style={{ width: '100%', paddingBottom: '10px', paddingRight: '10px', marginTop: 'auto' }}>
                            {person.otherLinkedEntities && _.size(person.otherLinkedEntities) > 0 && (
                                <LinkedEntities
                                    count={_.size(person.otherLinkedEntities)}
                                    entities={person.otherLinkedEntities}
                                    onRemoveEntity={onRemoveEntity}
                                    onEditEntity={onEditEntity}
                                />
                            )}
                        </Box>

                    </>
                )}
            </CardContent>
        </Card>
    );
};

export default PersonCard;